<template>
  <div class="plan-downgrade">
    <div class="plan-title">
      <RouterLink to="/settings/payment/plans" class="plan-title__link">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="black"/>
        </svg>
      </RouterLink>
      <span>{{ $t('changePlan') }}</span>

    </div>
    <Preloader v-if="localLoading"/>
    <div v-show="!localLoading" v-if="currentPlan && plan">
      <div class="plan-subtitle">
        {{
          $t('subject', {
            plan: currentPlan.title,
            nextPlan: plan.title,
            expirationText: expirationText
          })
        }}
      </div>
      <PaymentDialogPeriods v-if="plan.price" @input="period = $event" :plan="plan"/>
      <PaymentMethodsSelector v-if="plan.price && period" @input="onPaymentMethodChange"/>
      <StripePaymentCard v-if="needNewCard"/>
      <VBtn @click="downgrade" v-if="period || !plan.price" class="plan-downgrade__btn">
        {{ $t('confirm') }}
      </VBtn>
    </div>
  </div>
</template>

<script>
import useDowngradePlan from '@/compositions/accounting/useDowngradePlan';
import useValidation from '@/compositions/useValidation';
import Preloader from '@/components/Preloader';
import useAccount from '@/compositions/accounting/useAccount';
import { computed, ref } from '@vue/composition-api';
import PaymentDialogPeriods from '@/components/Payment/Periods/PaymentDialogPeriods';
import PaymentMethodsSelector from '@/components/Payment/Methods/PaymentMethodsCardSelector';
import StripePaymentCard from '@/components/Payment/Payment/Stripe/StripePaymentCard';
import useStripe from '@/compositions/accounting/useStripe';
import { orderPlanOperations, orderTypes } from '@/enums';

export default {
  name: 'PlanDowngrade',
  components: {
    StripePaymentCard,
    PaymentMethodsSelector,
    PaymentDialogPeriods,
    Preloader,
  },
  props: {
    plan: {
      type: Object,
      required: true,
    },
  },
  setup({ plan }, {
    emit,
    root: { $router },
  }) {
    const {
      expirationText,
      account,
      period: currentPeriod,
    } = useAccount();
    const { downgradePlan } = useDowngradePlan();
    const {
      withLoading,
      localLoading,
    } = useValidation();
    const { createPaymentMethod } = useStripe();
    const periodRef = ref(null);
    const period = computed({
      get() {
        return periodRef.value || currentPeriod.value;
      },
      set(val) {
        periodRef.value = val;
      },
    });
    const method = ref(null);

    const needNewCard = ref(false);

    function downgrade() {
      withLoading(async () => {
        let paymentMethodId = method.value?.id;
        if (needNewCard.value) {
          paymentMethodId = await createPaymentMethod();
        }

        await downgradePlan({
          planId: plan.id,
          periodId: period.value?.id,
          paymentMethodId: paymentMethodId || method.value,
        });

        $router.replace({
          name: 'order-success',
          params: {
            id: plan.id,
            operation: orderPlanOperations.DOWNGRADE,
            type: orderTypes.PLAN,
          },
        });

        emit('success');
      });
    }

    function onPaymentMethodChange(val) {
      if (val !== 'new') {
        method.value = val;
        needNewCard.value = false;
        return;
      }
      method.value = null;

      needNewCard.value = true;
    }

    return {
      downgrade,
      localLoading,
      expirationText,
      period,
      currentPlan: computed(() => account.value.plan),
      needNewCard,
      onPaymentMethodChange,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-downgrade {
  font-weight: 400;
  padding: 2.125rem 2.5rem;
}
.plan-title {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  line-height: 1.2;
  color: #212121;
  margin-bottom: 0.9375rem;
  &__link {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.5rem;
    svg {
      max-width: 100%;
      max-height: 100%;
    }
  }
}
.plan-subtitle {
  font-size: 1.0625rem;
  line-height: 1.53;
  color: #091A35;
  margin-bottom: 1.125rem;
}
@media #{map-get($display-breakpoints, 'md-and-down')} {
  .plan-downgrade {
    padding: 1.25rem 0.625rem;
    &__btn {
      margin: 0 0.625rem;
    }
  }
  .plan-title {
    padding: 0 0.625rem;
    font-size: 1.25rem;
    &__link {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
  .plan-subtitle {
    padding: 0 0.625rem;
    font-size: 0.9375rem;
    line-height: 1.39;
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .plan-downgrade {
    padding: 1.25rem 0.625rem;
  }

  .plan-title {
    padding: 0 0.625rem;
    font-size: 1rem;
    margin-bottom: 0.875rem;
    &__link {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
  .plan-subtitle {
    font-size: 0.8125rem;
    line-height: 1.39;
    margin-bottom: 0.75rem;
  }
}

</style>

<i18n lang="yml" locale="ru">
changePlan: Изменение тарифного плана
subject: 'Сейчас у вас оформлена подписка на тариф «{plan}». Переход на тарифный план «{nextPlan}» произойдёт после завершения текущего оплаченного периода {expirationText}. Вы можете отменить переход на «{nextPlan}» в любое время до момента первого списания денег по новому тарифу (за сутки до завершения текущего платежного периода).'
confirm: Подтвердить
</i18n>
<i18n lang="yml" locale="en">
changePlan: Change plan
subject: 'You now have a subscription for the plan “{plan}”. You will be automatically switched to the plan “{nextPlan}” at the end of the current pay period {expirationText}. You can cancel the change at any time until the first payment on your new plan (1 day before the end of the current pay period).'
confirm: Confirm
</i18n>
